import { CategoryDimensionScores } from '../../../types/metrics'
import InfoTooltip from '../../atoms/InfoTooltip'

const SCORES_BY_CATEGORY_TOOLTIP =
  'Average score per dimension, broken down by job category. Cells are color-coded: green ≥ 80, yellow 65–79, red < 65.'

type Props = {
  rows: CategoryDimensionScores[]
}

type DimKey = keyof Omit<CategoryDimensionScores, 'category' | 'reviewCount'>

const DIMS: { key: DimKey; label: string }[] = [
  { key: 'growthMindset', label: 'Growth Mindset' },
  { key: 'communication', label: 'Communication' },
  { key: 'commitment', label: 'Commitment' },
  { key: 'professionalSkills', label: 'Prof. Skills' },
  { key: 'productivity', label: 'Productivity' },
]

/** Maps a 0–100 score to inline styles for the heat-map cells. */
function scoreStyle(score: number | null): React.CSSProperties {
  if (score === null) return { backgroundColor: '#F3F4F6', color: '#9CA3AF' }
  if (score >= 80) return { backgroundColor: '#BBF7D0', color: '#166534' }
  if (score >= 65) return { backgroundColor: '#FEF08A', color: '#854D0E' }
  return { backgroundColor: '#FECACA', color: '#991B1B' }
}

const CategoryMatrixTable = ({ rows }: Props): JSX.Element => {
  if (rows.length === 0) {
    return (
      <div className="rounded-2xl bg-white p-8 shadow-[0_0_20px_5px_rgba(0,0,0,0.08)]">
        <div className="mb-4 flex items-center gap-2">
          <h3 className="text-2xl font-bold text-dark-blue">Scores by category</h3>
          <InfoTooltip text={SCORES_BY_CATEGORY_TOOLTIP} />
        </div>
        <p className="text-xl text-stone-gray">No data available.</p>
      </div>
    )
  }

  return (
    <div className="rounded-2xl bg-white p-8 shadow-[0_0_20px_5px_rgba(0,0,0,0.08)]">
      <div className="mb-4 flex items-center gap-2">
        <h3 className="text-2xl font-bold text-dark-blue">Scores by category</h3>
        <InfoTooltip text={SCORES_BY_CATEGORY_TOOLTIP} />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full" aria-label="Dimension scores by category">
          <thead>
            <tr className="border-b-2 border-soft-gray">
              <th className="pb-3 text-left text-xl font-semibold text-blue-gray">Category</th>
              <th className="pb-3 text-center text-xl font-semibold text-blue-gray">Reviews</th>
              {DIMS.map(d => (
                <th key={d.key} className="pb-3 text-center text-xl font-semibold text-blue-gray">
                  {d.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.category} className="border-b border-soft-gray last:border-0">
                <td className="py-3 pr-4 text-xl font-semibold text-dark-blue">{row.category}</td>
                <td className="py-3 text-center text-xl text-stone-gray">{row.reviewCount}</td>
                {DIMS.map(d => {
                  const score = row[d.key]
                  return (
                    <td key={d.key} className="px-1 py-3 text-center">
                      <span
                        className="inline-block min-w-[3.5rem] rounded-md px-3 py-1 text-xl font-bold"
                        style={scoreStyle(score)}
                      >
                        {score !== null ? score : '–'}
                      </span>
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-4 flex items-center gap-4 text-xl text-stone-gray">
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-3 w-3 rounded" style={{ backgroundColor: '#BBF7D0' }} />
          Strong (≥ 80)
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-3 w-3 rounded" style={{ backgroundColor: '#FEF08A' }} />
          Developing (65–79)
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-3 w-3 rounded" style={{ backgroundColor: '#FECACA' }} />
          Needs focus (&lt; 65)
        </span>
      </div>
    </div>
  )
}

export const CategoryMatrixTableSkeleton = (): JSX.Element => (
  <div aria-hidden="true" className="rounded-2xl bg-white p-8 shadow-[0_0_20px_5px_rgba(0,0,0,0.08)]">
    <div className="mb-4 h-7 w-1/3 rounded bg-soft-gray animate-pulse" />
    <div className="flex flex-col gap-3">
      {[0, 1, 2, 3, 4].map(i => (
        <div key={i} className="flex gap-3">
          <div className="h-6 w-1/4 rounded bg-soft-gray animate-pulse" />
          {[0, 1, 2, 3, 4, 5].map(j => (
            <div key={j} className="h-6 flex-1 rounded bg-soft-gray animate-pulse" />
          ))}
        </div>
      ))}
    </div>
  </div>
)

export default CategoryMatrixTable
